import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import BpmnModeler from "bpmn-js/lib/Modeler";
import propertiesPanelModule from "bpmn-js-properties-panel";
import camundaModdleDescriptor from "camunda-bpmn-moddle/resources/camunda";
import minimapModule from "diagram-js-minimap";
import React, { useState, useEffect, useRef, useCallback } from "react";

import { emptyBpmn } from "../../../../assets/empty.bpmn";
import BpmnControls from "../../../../components/smart/camunda/BpmnControls";
import colorRenderer from "../../../../components/smart/camunda/colors";
import customTranslate from "../../../../components/smart/camunda/modelerTranslate/customTranslate";
import propertiesProviderModule from "../../../../components/smart/camunda/provider";
import GenericSnackbar from "../../../../components/snackbar/generic-snackbar";
import { language } from "../../../../language";

import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-font/dist/css/bpmn-embedded.css";
import "./bpmn-fileControls.css";
import "./bpmn-js-properties-panel.css";
import "./modeler-properties-panel.css";
import "../manage-process-page/form-modeler/styles.css";
import "../../../../components/smart/camunda/colors/color-picker.css";

// Language constants defined outside the component for better performance
const {
  processNoname,
  idProccesInvalid,
  userTaskKeyIsInvalid,
  taskKeyDuplicate,
  xmlFail,
} = language;

const Modeler = (props) => {
  const {
    // Props from Redux state
    processDeployment,
    apiError,

    // Dispatcher props from Redux
    deployProcess,
  } = props;

  // Refs to hold the modeler instance and zoom scale without causing re-renders
  const modelerRef = useRef(null);
  const scale = useRef(1);

  // State managed by useState hooks
  const [propClassName, setPropClassName] = useState("normal");
  const [modelerRowSize, setModelerRowSize] = useState(9);
  const [messageError, setMessageError] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Core Modeler Functions ---

  const GetProcessName = useCallback(() => {
    if (!modelerRef.current) return "";
    const defs = modelerRef.current.getDefinitions();
    const process = defs.rootElements.find((el) => el.$type === "bpmn:Process");
    return process ? process.name : "";
  }, []);

  const openBpmnDiagram = useCallback(
    (xml) => {
      if (!modelerRef.current) return;
      try {
        modelerRef.current.importXML(xml).then(() => {
          const canvas = modelerRef.current.get("canvas");
          canvas.zoom("fit-viewport");
        });
      } catch (err) {
        setMessageError(xmlFail);
      }
    },
    [xmlFail],
  );

  // --- Validation Logic ---

  const findErrorInProcess = useCallback(() => {
    const modeler = modelerRef.current;
    if (!modeler) return { isValid: false, Error: "Modeler not initialized." };

    const defs = modeler.getDefinitions();
    const process = defs.rootElements.find((el) => el.$type === "bpmn:Process");

    if (!process || process.id === "Process_1" || !process.id?.trim()) {
      return { isValid: false, Error: idProccesInvalid };
    }
    if (!process.name?.trim()) {
      return { isValid: false, Error: processNoname };
    }

    const userTasks =
      process.flowElements?.filter((el) => el.$type === "bpmn:UserTask") || [];
    const taskKeys = userTasks.map((task) => task.formKey).filter(Boolean);
    const duplicateKey = taskKeys.find(
      (key, index) => taskKeys.indexOf(key) !== index,
    );

    if (duplicateKey) {
      const activity = userTasks.find((task) => task.formKey === duplicateKey);
      return {
        isValid: false,
        Error: taskKeyDuplicate.replace(
          "${activityIdOrName}",
          activity.name || activity.id,
        ),
      };
    }

    for (const task of userTasks) {
      const hasFormData = task.extensionElements?.values?.some(
        (ext) => ext.$type === "camunda:FormData",
      );
      if (hasFormData && !task.formKey?.trim()) {
        return {
          isValid: false,
          Error: userTaskKeyIsInvalid.replace(
            "${activityIdOrName}",
            task.name || task.id,
          ),
        };
      }
    }

    return { isValid: true, Error: "" };
  }, [idProccesInvalid, processNoname, taskKeyDuplicate, userTaskKeyIsInvalid]);

  // --- Event Handlers ---

  const handleSaveFile = useCallback(
    (e) => {
      e.preventDefault();
      if (!modelerRef.current) return;
      modelerRef.current.saveXML({ format: true }).then(({ xml }) => {
        const encodedData = encodeURIComponent(xml);
        const a = document.createElement("a");
        a.href = `data:application/bpmn20-xml;charset=UTF-8,${encodedData}`;
        a.download = `${GetProcessName() || "diagram"}.bpmn`;
        a.click();
        a.remove();
      });
    },
    [GetProcessName],
  );

  const handleSaveImage = useCallback(
    (e) => {
      e.preventDefault();
      if (!modelerRef.current) return;
      modelerRef.current.saveSVG().then(({ svg }) => {
        const encodedData = encodeURIComponent(svg);
        const a = document.createElement("a");
        a.href = `data:application/bpmn20-xml;charset=UTF-8,${encodedData}`;
        a.download = `${GetProcessName() || "diagram"}.svg`;
        a.click();
        a.remove();
      });
    },
    [GetProcessName],
  );

  const handleZoom = useCallback((newScale) => {
    if (modelerRef.current) {
      scale.current = newScale;
      modelerRef.current.get("canvas").zoom(scale.current);
    }
  }, []);

  const propertiesPanelWrapper = useCallback(() => {
    setModelerRowSize((prevSize) => (prevSize === 12 ? 9 : 12));
    setPropClassName((prevClass) =>
      prevClass === "normal" ? "hidden" : "normal",
    );
  }, []);

  const snackClose = useCallback(() => {
    setMessageError(null);
  }, []);

  useEffect(() => {
    document.body.className = "shown";
    modelerRef.current = new BpmnModeler({
      container: "#bpmnview",
      keyboard: { bindTo: window },
      propertiesPanel: { parent: "#propview" },
      additionalModules: [
        propertiesPanelModule,
        propertiesProviderModule,
        minimapModule,
        colorRenderer,
        { translate: ["value", customTranslate] },
      ],
      moddleExtensions: { camunda: camundaModdleDescriptor },
    });

    openBpmnDiagram(emptyBpmn);
    setLoading(true);
    return () => {
      // Cleanup on unmount
      modelerRef.current?.destroy();
      document.body.className = "";
    };
  }, [openBpmnDiagram]);

  const message =
    messageError || apiError || (processDeployment && language.pulishSucess);
  const showSnackbar = !!(messageError || apiError || processDeployment);

  return (
    <Box sx={{ width: "100%", height: "100%" }} id="modelerbuilder">
      <Grid container direction="row" sx={{ width: "100%", height: "100%" }}>
        <Grid item size="grow" xs={modelerRowSize} className="panelmain">
          {loading && (
            <BpmnControls
              modeler={modelerRef.current}
              onSaveFile={handleSaveFile}
              onSaveImage={handleSaveImage}
              onRedo={() => modelerRef.current?.get("commandStack").redo()}
              onUndo={() => modelerRef.current?.get("commandStack").undo()}
              onZoomIn={() => handleZoom(scale.current + 0.1)}
              onZoomOut={() => handleZoom(scale.current - 0.1)}
              onZoomReset={() => handleZoom(1)}
              onShowHidePropertiesPanel={propertiesPanelWrapper}
              processDeployment={processDeployment}
              findErrorInProcess={findErrorInProcess}
            />
          )}
          <Box id="bpmnview" sx={{ height: "100vh" }} />
        </Grid>
        <Grid item xs={3} className={`panelprops ${propClassName}`}>
          <div id="propview" className={`properties ${propClassName}`} />
        </Grid>
      </Grid>

      {showSnackbar && (
        <GenericSnackbar
          success={!!processDeployment}
          error={!!(messageError || apiError)}
          message={message}
          handleClose={snackClose}
          duration={3000}
        />
      )}
    </Box>
  );
};

export default Modeler;
